// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';
import copy from 'clipboard-copy';

import {reporter} from 'common/report/error-reporting';
import {Button, ButtonJsxProps} from 'common/components/button';
import {ToastType} from 'common/components/toast/types';
import {addToast} from 'common/components/toast-area/toast-manager';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {IronContextRegistry} from 'common/report/context-registry';

import {CopyOption, CopyType} from './types';
import {template} from './template';
import css from './index.inline.styl';

interface CopyToClipboardButtonJsxProps {
  on?: {
    copy?: (value) => void;
  };
  attrs: {
    'copy-type'?: CopyType;
    options: CopyOption[];
  } & ButtonJsxProps[`attrs`];
}

export class CopyToClipboardButton extends Component<
  EmptyObj,
  CopyToClipboardButtonJsxProps[`attrs`],
  null,
  null,
  IronContextRegistry
> {
  static get attrsSchema(): AttrsSchema<CopyToClipboardButtonJsxProps[`attrs`]> {
    return {
      ...Button.attrsSchema,
      'copy-type': {type: `string`, enum: Object.values(CopyType), default: CopyType.Copy},
      options: {
        description: `The options for what to copy with their values and labels`,
        type: `json`,
      },
    };
  }

  get i18n() {
    return this.getContext(`i18n`);
  }

  get t() {
    return this.i18n.t;
  }

  get print() {
    return this.i18n.print;
  }

  get config() {
    return {
      useShadowDom: true,
      css,
      template,
      contexts: [`i18n` as const],
      helpers: {
        copyValue: async (value) => {
          try {
            await copy(value);
            this.dispatchEvent(
              new CustomEvent(`copy`, {
                detail: {
                  value,
                },
              }),
            );
            addToast({
              type: ToastType.SUCCESS,
              message:
                this.attr(`copy-type`) === CopyType.Link
                  ? this.t(`components.copy-to-clipboard.url-copied`)
                  : this.t(`components.copy-to-clipboard.copied`),
            });
          } catch (err) {
            reporter.error(err);
            addToast({
              type: ToastType.SUCCESS,
              message:
                this.attr(`copy-type`) === CopyType.Link
                  ? this.t(`components.copy-to-clipboard.url-copy-error`)
                  : this.t(`components.copy-to-clipboard.copy-error`),
            });
          }
        },
      },
    };
  }
}

export const CopyToClipboardButtonEl = defineAndMakeJsxFactory<CopyToClipboardButtonJsxProps>(
  `mp-copy-to-clipboard-button`,
  CopyToClipboardButton,
);
export const CopyToClipboardButtonReact = panel2react<CopyToClipboardButtonJsxProps>(
  `mp-copy-to-clipboard-button`,
  CopyToClipboardButton,
);
